import type React from 'react';
import type { ConversationDTO, MessageDTO } from '@tc/contracts';
import { getState } from '../store.ts';
import { seal, translate, type MsgKey } from '../i18n.ts';

export function Seal({ locale, title, small = false }: { locale: string; title?: string; small?: boolean }) {
  return (
    <span className={`seal seal-${locale} ${small ? 'seal-sm' : ''}`} title={title} aria-label={title}>
      {seal(locale)}
    </span>
  );
}

export function conversationTitle(c: ConversationDTO, meId: string | undefined): string {
  if (c.type === 'group') return c.title ?? '';
  const other = c.members.find((m) => m.id !== meId);
  return other?.displayName ?? '—';
}

/** Distinct reading languages in a conversation (for the seal stack). */
export function conversationLocales(c: ConversationDTO, meId: string | undefined): string[] {
  const others = c.members.filter((m) => m.id !== meId);
  return [...new Set(others.map((m) => m.readingLocale))];
}

export function displayName(userId: string | null | undefined, ui: string): string {
  if (!userId) return translate(ui, 'someone');
  const s = getState();
  if (userId === s.me?.id) return translate(ui, 'you');
  return s.users[userId]?.displayName ?? translate(ui, 'someone');
}

export function systemText(m: MessageDTO, ui: string): string {
  const p = m.system;
  if (!p) return '';
  const actor = displayName(p.actorId, ui);
  const users = (p.userIds ?? []).map((u) => displayName(u, ui)).join(', ');
  const key = `sys_${p.event}` as MsgKey;
  return translate(ui, key, { actor, users, title: p.title ?? '' });
}

/** What a reader sees by default: their translation when ready, else the original. */
export function readableBody(m: MessageDTO): string {
  if (m.translation?.status === 'ready' && m.translation.body) return m.translation.body;
  if (!m.body?.trim()) {
    if (m.attachment) return `${m.attachment.kind === 'image' ? '🖼' : '📄'} ${m.attachment.name ?? ''}`.trim();
    if (m.audio) return '🎤';
  }
  return m.body ?? '';
}

const URL_RE = /\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]]/giu;

/** Render text with safe links (http/https only). React escapes everything else. */
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Highlight "@Name" for the given member names (the one that is me gets an extra class). */
function withMentions(text: string, names: string[] | undefined, myName: string | undefined, keyBase: number): React.ReactNode[] {
  if (!names?.length || !text.includes('@')) return [text];
  const re = new RegExp(`@(?:${[...names].sort((a, b) => b.length - a.length).map(esc).join('|')})`, 'gu');
  const out: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const i = m.index ?? 0;
    if (i > last) out.push(text.slice(last, i));
    out.push(<span key={`${keyBase}-${i}`} className={`mention ${m[0] === `@${myName}` ? 'me' : ''}`}>{m[0]}</span>);
    last = i + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Linkified({ text, names, myName }: { text: string; names?: string[]; myName?: string }) {
  const parts: Array<string | { url: string }> = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    const i = m.index ?? 0;
    if (i > last) parts.push(text.slice(last, i));
    parts.push({ url: m[0] });
    last = i + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return (
    <>
      {parts.map((p, i) =>
        typeof p === 'string' ? (
          withMentions(p, names, myName, i)
        ) : (
          <a key={i} href={p.url} target="_blank" rel="noopener noreferrer nofollow">
            {p.url}
          </a>
        ),
      )}
    </>
  );
}
